/**
 * Geometry shared by the browser preview and the server renderer, so that
 * what the Preview canvas shows is what goes on air.
 *
 * Server model (castr-studio obs-worker `normalize()` + render-dom.js):
 * - A source castr adds with `fitToScreen` has bounds = the full canvas and
 *   its content is "contain"-fitted (letterboxed) inside.
 * - Once castr applies a transform it sets `boundsType = OBS_BOUNDS_NONE`, so
 *   the box is (positionX, positionY, canvasW * scaleX/100, canvasH * scaleY/100)
 *   in canvas pixels and the content is contain-fitted inside that box.
 * - Browser (URL) sources render a 1920x1080 viewport scaled by
 *   min(boxW/1920, boxH/1080) and centred in the box.
 * - z-order: castr syncs `flow.media` reversed and reorders with
 *   `(mediaCount - newIndex) - 1`, so `media[0]` is the TOP layer and the last
 *   item is the bottom one. Hidden (`show === false`) items are not drawn.
 */

export const URL_VIEWPORT = { width: 1920, height: 1080 }

export const DEFAULT_TRANSFORM = Object.freeze({ positionX: 0, positionY: 0, scaleX: 100, scaleY: 100 })

const num = (v, fallback = 0) => {
	const n = typeof v === 'string' ? parseFloat(v) : Number(v)
	return Number.isFinite(n) ? n : fallback
}

/** Normalised copy of a media transform, or null when the source is still fit-to-screen. */
export const normalizeTransform = (transform) => {
	if (!transform || typeof transform !== 'object') return null
	return {
		positionX: num(transform.positionX, 0),
		positionY: num(transform.positionY, 0),
		scaleX: num(transform.scaleX, 100),
		scaleY: num(transform.scaleY, 100),
	}
}

/** Box in canvas pixels: { x, y, w, h }. */
export const layerBox = (transform, canvas) => {
	const t = normalizeTransform(transform)
	if (!t) return { x: 0, y: 0, w: canvas.width, h: canvas.height }
	return {
		x: t.positionX,
		y: t.positionY,
		w: (canvas.width * t.scaleX) / 100,
		h: (canvas.height * t.scaleY) / 100,
	}
}

const round2 = (n) => Math.round(n * 100) / 100

/** Inverse of `layerBox`: a box in canvas pixels → the `{transform}` payload castr stores. */
export const boxToTransform = (box, canvas) => ({
	positionX: Math.round(box.x),
	positionY: Math.round(box.y),
	scaleX: round2((box.w / canvas.width) * 100),
	scaleY: round2((box.h / canvas.height) * 100),
})

/** Where content of a given aspect ratio lands inside a box with object-fit: contain. */
export const containRect = (box, contentAspect) => {
	if (!contentAspect || !Number.isFinite(contentAspect) || contentAspect <= 0) return { ...box }
	const boxAspect = box.w / box.h
	if (boxAspect > contentAspect) {
		const w = box.h * contentAspect
		return { x: box.x + (box.w - w) / 2, y: box.y, w, h: box.h }
	}
	const h = box.w / contentAspect
	return { x: box.x, y: box.y + (box.h - h) / 2, w: box.w, h }
}

/** Scale factor + offset for a 1920x1080 browser viewport inside a box (render-dom.js rule). */
export const urlViewportFit = (box, viewport = URL_VIEWPORT) => {
	const s = Math.min(box.w / viewport.width, box.h / viewport.height)
	return {
		scale: s,
		offsetX: (box.w - viewport.width * s) / 2,
		offsetY: (box.h - viewport.height * s) / 2,
	}
}

/** Render order, bottom → top. `media[0]` airs on top. */
export const renderOrder = (media) => (Array.isArray(media) ? [...media].reverse() : [])

/** CSS z-index for `media[index]` (higher = closer to the viewer). */
export const zIndexOf = (media, index) => (Array.isArray(media) ? media.length - index : 0)

/** Same transform? (used to count real draft changes). */
export const sameTransform = (a, b) => {
	const na = normalizeTransform(a)
	const nb = normalizeTransform(b)
	if (!na && !nb) return true
	if (!na || !nb) return false
	return ['positionX', 'positionY', 'scaleX', 'scaleY'].every((k) => Math.abs(na[k] - nb[k]) < 0.005)
}

/** Keep a box inside sane limits (min 2 % of the canvas; may overflow the edges like OBS). */
export const clampBox = (box, canvas) => {
	const minW = canvas.width * 0.02
	const minH = canvas.height * 0.02
	return {
		x: box.x,
		y: box.y,
		w: Math.max(minW, box.w),
		h: Math.max(minH, box.h),
	}
}

/** Keep a box fully on the canvas: no bigger than the canvas, edges inside it. */
export const clampToCanvas = (box, canvas) => {
	const minW = canvas.width * 0.02
	const minH = canvas.height * 0.02
	const w = Math.min(canvas.width, Math.max(minW, box.w))
	const h = Math.min(canvas.height, Math.max(minH, box.h))
	return {
		x: Math.min(canvas.width - w, Math.max(0, box.x)),
		y: Math.min(canvas.height - h, Math.max(0, box.y)),
		w,
		h,
	}
}

/**
 * Clamp a moved box without resizing it: a box that fits stays inside the
 * canvas; one larger than the canvas (Fill) keeps covering it.
 */
export const clampMove = (box, canvas) => {
	const axis = (pos, size, room) => (size <= room ? Math.min(room - size, Math.max(0, pos)) : Math.min(0, Math.max(room - size, pos)))
	return { ...box, x: axis(box.x, box.w, canvas.width), y: axis(box.y, box.h, canvas.height) }
}

/** Same, for a stored `{transform}` (null = fit-to-screen, already on canvas). */
export const clampTransform = (transform, canvas) => {
	if (!normalizeTransform(transform)) return transform
	return boxToTransform(clampToCanvas(layerBox(transform, canvas), canvas), canvas)
}

/**
 * Clamp a resized box to the canvas. Only the dragged edges move; the opposite
 * edges stay anchored. With `keepAspect` a corner resize shrinks uniformly so
 * the ratio survives hitting an edge.
 */
export const clampResize = (box, start, handle, canvas, keepAspect) => {
	const minW = canvas.width * 0.02
	const minH = canvas.height * 0.02
	const hasW = handle.includes('w')
	const hasE = handle.includes('e')
	const hasN = handle.includes('n')
	const hasS = handle.includes('s')
	const right = start.x + start.w
	const bottom = start.y + start.h
	let { w, h } = box
	// Room available from the anchored edges to the canvas edges.
	const maxW = hasW ? Math.max(minW, right) : hasE ? Math.max(minW, canvas.width - start.x) : canvas.width
	const maxH = hasN ? Math.max(minH, bottom) : hasS ? Math.max(minH, canvas.height - start.y) : canvas.height
	if (keepAspect && (hasW || hasE) && (hasN || hasS)) {
		const aspect = start.w / start.h
		const s = Math.min(1, maxW / w, maxH / h)
		w *= s
		h *= s
		if (w < minW) { w = minW; h = w / aspect }
		if (h < minH) { h = minH; w = h * aspect }
	} else {
		w = Math.min(maxW, Math.max(minW, w))
		h = Math.min(maxH, Math.max(minH, h))
	}
	const x = hasW ? right - w : hasE ? start.x : box.x
	const y = hasN ? bottom - h : hasS ? start.y : box.y
	return clampToCanvas({ x, y, w, h }, canvas)
}

/** Lines a box can snap to: canvas edges/centre plus other layers' edges/centres. Build once per gesture. */
export const snapLines = (canvas, others = []) => {
	const xs = new Set([0, canvas.width / 2, canvas.width])
	const ys = new Set([0, canvas.height / 2, canvas.height])
	others.forEach((o) => {
		xs.add(o.x); xs.add(o.x + o.w / 2); xs.add(o.x + o.w)
		ys.add(o.y); ys.add(o.y + o.h / 2); ys.add(o.y + o.h)
	})
	return { xs: [...xs], ys: [...ys] }
}

/**
 * Layout templates applied to ONE selected layer. Positions in canvas px,
 * sizes as % of canvas (what the server expects).
 */
export const layoutTemplates = (canvas) => {
	const W = canvas.width
	const H = canvas.height
	const portrait = H > W
	const m = Math.round(Math.min(W, H) * 0.025) // edge margin
	const pip = portrait ? 40 : 30 // PiP size in %
	const pipW = (W * pip) / 100
	const pipH = (H * pip) / 100
	const t = (x, y, sx, sy) => ({ positionX: Math.round(x), positionY: Math.round(y), scaleX: round2(sx), scaleY: round2(sy) })
	return [
		{ key: 'full', label: 'Fullscreen', group: 'layout', transform: t(0, 0, 100, 100) },
		{ key: 'pip-tl', label: 'PiP top left', group: 'pip', transform: t(m, m, pip, pip) },
		{ key: 'pip-tr', label: 'PiP top right', group: 'pip', transform: t(W - pipW - m, m, pip, pip) },
		{ key: 'pip-bl', label: 'PiP bottom left', group: 'pip', transform: t(m, H - pipH - m, pip, pip) },
		{ key: 'pip-br', label: 'PiP bottom right', group: 'pip', transform: t(W - pipW - m, H - pipH - m, pip, pip) },
		{ key: 'side-left', label: 'Side-by-side (left)', group: 'layout', transform: t(0, H * 0.25, 50, 50) },
		{ key: 'side-right', label: 'Side-by-side (right)', group: 'layout', transform: t(W / 2, H * 0.25, 50, 50) },
		{ key: 'lower-third', label: 'Lower third', group: 'overlay', transform: t(0, H * (2 / 3), 100, 100 / 3) },
		{ key: 'l-band', label: 'L-band content', group: 'layout', transform: t(W * 0.25, 0, 75, 75) },
		{ key: 'logo-bug', label: 'Logo bug', group: 'overlay', transform: t(W - W * 0.12 - m, m, 12, 12) },
	]
}

/**
 * Snap a box to the canvas edges/centre and to other layers' edges/centres.
 * `mode` is 'move' (whole box slides) or 'resize' (only the dragged edges move).
 * Returns the adjusted box plus the guide lines to draw (canvas px).
 */
export const snapBox = (box, { canvas, others = [], lines = null, threshold = 8, mode = 'move', edges = null } = {}) => {
	const { xs, ys } = lines || snapLines(canvas, others)
	const guides = []
	const result = { ...box }

	const nearest = (lines, value) => {
		let best = null
		lines.forEach((line) => {
			const d = Math.abs(line - value)
			if (d <= threshold && (best === null || d < best.d)) best = { line, d }
		})
		return best
	}

	if (mode === 'move') {
		const candX = [
			{ value: box.x, apply: (line) => { result.x = line } },
			{ value: box.x + box.w / 2, apply: (line) => { result.x = line - box.w / 2 } },
			{ value: box.x + box.w, apply: (line) => { result.x = line - box.w } },
		]
		let bestX = null
		candX.forEach((c) => { const n = nearest(xs, c.value); if (n && (!bestX || n.d < bestX.d)) bestX = { ...n, apply: c.apply } })
		if (bestX) { bestX.apply(bestX.line); guides.push({ axis: 'x', pos: bestX.line }) }

		const candY = [
			{ value: box.y, apply: (line) => { result.y = line } },
			{ value: box.y + box.h / 2, apply: (line) => { result.y = line - box.h / 2 } },
			{ value: box.y + box.h, apply: (line) => { result.y = line - box.h } },
		]
		let bestY = null
		candY.forEach((c) => { const n = nearest(ys, c.value); if (n && (!bestY || n.d < bestY.d)) bestY = { ...n, apply: c.apply } })
		if (bestY) { bestY.apply(bestY.line); guides.push({ axis: 'y', pos: bestY.line }) }
		return { box: result, guides }
	}

	// resize: snap only the edges being dragged
	const e = edges || {}
	if (e.left) { const n = nearest(xs, box.x); if (n) { result.w = box.x + box.w - n.line; result.x = n.line; guides.push({ axis: 'x', pos: n.line }) } }
	if (e.right) { const n = nearest(xs, box.x + box.w); if (n) { result.w = n.line - box.x; guides.push({ axis: 'x', pos: n.line }) } }
	if (e.top) { const n = nearest(ys, box.y); if (n) { result.h = box.y + box.h - n.line; result.y = n.line; guides.push({ axis: 'y', pos: n.line }) } }
	if (e.bottom) { const n = nearest(ys, box.y + box.h); if (n) { result.h = n.line - box.y; guides.push({ axis: 'y', pos: n.line }) } }
	return { box: result, guides }
}

/** Source URL for a VOD media item (same rule as castr's obsSync). */
export const vodSourceUrl = (media) => {
	if (!media) return ''
	if (media.downloadUrl) return media.downloadUrl
	if (media.key && media.file) return `https://castr-videos.global.ssl.fastly.net/vod_uploads/${media.key}/${media.file}`
	return ''
}

/** Layers that decode video (live, VOD, web page) — used for the Lightweight suggestion. */
export const heavyLayerCount = (media) => (Array.isArray(media) ? media.filter((m) => m && m.show !== false && ['live', 'vod', 'url'].includes(m.type)).length : 0)
